import {gsap} from "gsap";
import {Signal} from "../Core/Signal";
import {Callback, Dict} from "../Core/Reflection";
import {Ease} from "../Core/Animatable";
import {Country, ItemModel, ItemType, ItemTypes, PackageModel} from "../Data/ItemModel";
import {PuzzleModel, PuzzleType} from "../GameObjects/Puzzle/PuzzleModel";
import {StateName} from "../Data/StateName";
import {RobotModel, RobotSlots} from "../GameObjects/Assembly/RobotModel";
import {MusicType} from "../GameObjects/Music/MusicModel";
import {CookingType} from "../GameObjects/Cooking/CookingModel";
import {MonsterModel, MonsterSlots} from "../GameObjects/Assembly/MonsterModel";
import {AssemblyType} from "../GameObjects/Assembly/AssemblyModel";
import {GameStorage} from "../Data/GameStorage";
import {StorageModel} from "../Data/StorageModel";

export class GameManager {

    public static readonly instance = new GameManager();
    public readonly onPackageArrived = Signal<Callback>();
    public readonly onPackageProgress = Signal<(value: number) => void>();
    private readonly _items: Dict<ItemType, ItemModel>;
    private readonly _packages: PackageModel[] = [
        {stateName: StateName.Kitchen, country: Country.Thailand, itemType: ItemType.Durian, itemData: {cookingType: CookingType.Durian}, label: "Durian Ice Cream"},
        {stateName: StateName.Robot, country: Country.Japan, itemType: ItemType.Robot, itemData: {assemblyType: AssemblyType.Robot, slots: {}}, label: "Robot"},
        {stateName: StateName.Puzzle, country: Country.Ghana, itemType: ItemType.Puzzle, itemData: {puzzleType: PuzzleType.Type1, puzzleFrame: 1}, label: "Puzzle Ghana"},
        {stateName: StateName.Home, country: Country.Germany, itemType: ItemType.Glockenspiel, itemData: {musicType: MusicType.Glockenspiel}, label: "Glockenspiel"},
        {stateName: StateName.Kitchen, country: Country.Italy, itemType: ItemType.Pasta, itemData: {cookingType: CookingType.Pasta}, label: "Pasta"},
        {stateName: StateName.Puzzle, country: Country.Argentina, itemType: ItemType.Puzzle, itemData: {puzzleType: PuzzleType.Type2, puzzleFrame: 1}, label: "Puzzle Argentina"},
        {stateName: StateName.Movie, country: Country.France, itemType: ItemType.Movie, itemData: {assemblyType: AssemblyType.Movie, slots: {}}, label: "Movie Maker"},
        {stateName: StateName.Puzzle, country: Country.China, itemType: ItemType.Puzzle, itemData: {puzzleType: PuzzleType.Type3, puzzleFrame: 1}, label: "Puzzle China"},
        {stateName: StateName.Monster, country: Country.Iceland, itemType: ItemType.Monster, itemData: {assemblyType: AssemblyType.Monster, slots: {}}, label: "Sea Monster"},
        {stateName: StateName.Home, country: Country.Australia, itemType: ItemType.Didgeridoo, itemData: {musicType: MusicType.Didgeridoo}, label: "Didgeridoo"},
        {stateName: StateName.Puzzle, country: Country.Switzerland, itemType: ItemType.Puzzle, itemData: {puzzleType: PuzzleType.Type4, puzzleFrame: 1}, label: "Puzzle Switzerland"},
        {stateName: StateName.Kitchen, country: Country.Polynesia, itemType: ItemType.Coconut, itemData: {cookingType: CookingType.Coconut}, label: "Coconut Milk"},
        {stateName: StateName.Puzzle, country: Country.Mexico, itemType: ItemType.Puzzle, itemData: {puzzleType: PuzzleType.Type5, puzzleFrame: 1}, label: "Puzzle Mexico"},
        {stateName: StateName.Home, country: Country.Scotland, itemType: ItemType.Bagpipes, itemData: {musicType: MusicType.Bagpipes}, label: "Bagpipes"},
        {stateName: StateName.Puzzle, country: Country.Russia, itemType: ItemType.Puzzle, itemData: {puzzleType: PuzzleType.Type6, puzzleFrame: 1}, label: "Puzzle Russia"},
        {stateName: StateName.Kitchen, country: Country.Turkey, itemType: ItemType.Delight, itemData: {cookingType: CookingType.Delight}, label: "Turkish Delight"},
        {stateName: StateName.Puzzle, country: Country.Egypt, itemType: ItemType.Puzzle, itemData: {puzzleType: PuzzleType.Type7, puzzleFrame: 1}, label: "Puzzle Egypt"},
        {stateName: StateName.Home, country: Country.Kenya, itemType: ItemType.Nyatiti, itemData: {musicType: MusicType.Nyatiti}, label: "Nyatiti"},
        {stateName: StateName.Puzzle, country: Country.Alaska, itemType: ItemType.Puzzle, itemData: {puzzleType: PuzzleType.Type8, puzzleFrame: 1}, label: "Puzzle Alaska"},
    ];
    private _packageIndex = 0;
    private _packageProgress = 1;
    private _packageTimeout = 0.1;
    private _isPackageArrived = true;
    private readonly _factVO: Dict<ItemType, number>;
    private readonly _completeActivityVO: Dict<ItemType, number>;
    private readonly _model: StorageModel;

    constructor() {
        this._model = GameStorage.model;
        this._packageIndex = this._model.packageIndex ?? 0;
        this._items = this._model.items ?? {};
        this._factVO = this._model.factVO ?? {};
        this._completeActivityVO = this._model.completeActivityVO ?? {};
        this.removeRussianPuzzle();
    }

    private removeRussianPuzzle() {
        const puzzlePackage = this._packages.filter(it => it.itemType === ItemType.Puzzle && it.country === Country.Russia)[0];
        const packageIndex = this._packages.indexOf(puzzlePackage);
        if (packageIndex >= 0) {
            this._packages.splice(packageIndex, 1);
        }
        if (this._packageIndex > packageIndex && !this._model.removedPuzzles) {
            this._packageIndex--;
        }
        const puzzleModel = this._items[ItemType.Puzzle]?.itemData as PuzzleModel;
        if (puzzleModel && puzzleModel.puzzleType === PuzzleType.Type6) {
            (puzzleModel as any).puzzleType = PuzzleType.Type5;
        }
        if (!this._model.removedPuzzles) {
            this._model.removedPuzzles = 1;
        }
    }

    private save() {
        this._model.packageIndex = this._packageIndex;
        this._model.items = this._items;
        this._model.factVO = this._factVO;
        this._model.completeActivityVO = this._completeActivityVO;
        GameStorage.model = this._model;
    }

    startPackageTimer() {
        this._packageProgress = 0;
        this._isPackageArrived = false;
        gsap.to(this, {
            duration: this._packageTimeout,
            ease: Ease.none,
            _packageProgress: 1,
            onUpdate: () => {
                this.onPackageProgress(this._packageProgress);
            },
            onComplete: () => {
                this._isPackageArrived = true;
                this.onPackageArrived();
            }
        });
    }

    get packageProgress() {
        return this._packageProgress;
    }

    /**
     * @deprecated it's the same as packageProgress === 1
     */
    get isPackageArrived() {
        return this._isPackageArrived;
    }

    get visits() {
        return this._packageIndex;
    }

    get visited() {
        return this._model.visited ?? false;
    }

    set visited(value: boolean) {
        if (this._model.visited !== value) {
            this._model.visited = value;
            this.save();
        }
    }

    get isFirstPackage() {
        return this._packageIndex === 0;
    }

    get allPackagesReceived() {
        return this._packageIndex >= this._packages.length;
    }

    get allPackages() {
        return this._packages;
    }

    getPackage() {
        return this._packages[this._packageIndex % this._packages.length];
    }

    addPackage(model: PackageModel) {
        const index = this._packages.indexOf(model);
        if (index < 0) {
            this._packageIndex = this._packages.length;
            this._packages.push(model);
        } else {
            this._packageIndex = index;
        }
    }

    nextPackage() {
        this._packageIndex++;
        this.save();
    }

    getEditPackage(item: ItemModel) {
        const packages = this._packages.filter(it => it.itemType === item.itemType);
        if (packages.length > 0) {
            switch (item.itemType) {
                case ItemType.Puzzle:
                    let puzzleType: PuzzleType;
                    if (this.allPackagesReceived) {
                        const puzzleTypes = this._packages.filter(it => it.itemType === ItemType.Puzzle).map(it => (it.itemData as PuzzleModel).puzzleType);
                        let i = puzzleTypes.indexOf((item.itemData as PuzzleModel).puzzleType) + 1;
                        if (i >= puzzleTypes.length) i = 0;
                        puzzleType = puzzleTypes[i];
                    } else {
                        puzzleType = (item.itemData as PuzzleModel).puzzleType;
                    }
                    return this._packages.filter(it => it.itemType === ItemType.Puzzle && (it.itemData as PuzzleModel).puzzleType === puzzleType)[0];
                default:
                    return {...packages[0], itemData: item.itemData};
            }
        }
        return undefined;
    }

    addItem(item: ItemModel) {
        this._items[item.itemType] = item;
        this.save();
    }

    getItem(itemType: ItemType) {
        return this._items[itemType];
    }

    hasItem(itemType: ItemType) {
        return !!this._items[itemType];
    }

    getItems() {
        return ItemTypes.map(it => this._items[it]).filter(it => !!it) as ItemModel[];
    }

    nextFactVO(type: ItemType, vo: string[]) {
        return this.nextVO(type, vo, this._factVO);
    }

    nextCompleteActivityVO(type: ItemType, vo: string[]) {
        return this.nextVO(type, vo, this._completeActivityVO);
    }

    private nextVO(type: ItemType, vo: string[], dict: Dict<ItemType, number>) {
        let index = dict[type];
        if (index === undefined) {
            index = 0;
            dict[type] = index;
        }
        index = index % vo.length;
        dict[type] = index + 1;
        this.save();
        return vo[index];
    }

    nthPackageOfType(itemType: ItemType) {
        const length = this._packages.length;
        const loops = Math.floor(this._packageIndex / length);
        const tail = this._packageIndex % length;
        const packagesOfType = this._packages.filter(it => it.itemType === itemType);
        let n = loops * packagesOfType.length;
        for (let i = 0; i < tail; i++) {
            if (this._packages[i].itemType === itemType) {
                n++;
            }
        }
        return n;
    }

    addAllItems() {
        this.addItem({
            itemType: ItemType.Robot,
            itemData: RobotSlots.reduce((a, t) => {
                a.slots[t] = 1;
                return a;
            }, {} as RobotModel)
        });
        this.addItem({
            itemType: ItemType.Monster,
            itemData: MonsterSlots.reduce((a, t) => {
                a.slots[t] = 1;
                return a;
            }, {} as MonsterModel)
        });
        this.addItem({itemType: ItemType.Glockenspiel, itemData: {musicType: MusicType.Glockenspiel}});
        this.addItem({itemType: ItemType.Durian, itemData: {cookingType: CookingType.Durian}});
        this.addItem({itemType: ItemType.Pasta, itemData: {cookingType: CookingType.Pasta}});
    }
}